<template>
  <div
    class="wt-asset-editor-view js-modal-content"
    :class="classObj"
    :style="styleObj"
  >
    <component :is="currentPageComponent" />
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's General Vue
 * Using VueJS
 */

import app from 'app';
import Vue from 'vue';
import bus from './AssetEditorEventBus';
import storyViewBus from 'v/Story/storyViewBus';
import pages from './pages/pages';
import openCrop from 'components/modals/types/cropping/wtCropping.modal';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { workPool } from '@wochitnpm/helpers';

export default Vue.extend({
  components: pages,

  data() {
    return {
      curtainsGap: app.CURTAINS_GAP + '%',
    };
  },

  props: {
    overlayGuid: { type: String },
  },

  computed: {
    ...mapGetters('assetEditor', [
      'currentPage',
      'blackListPages',
      'AGD',
      'isMouseDisabled',
      'assetModel',
      'story',
      'defaultEditorUrl',
      'tmpAGD',
      'presets',
      'rtpModel',
      'isDurationChanged',
      'shouldPullRTP',
      'calRTPChecksum',
      'calcSceneLayoutSTPChecksum',
      'analyticsData',
      'isDirty',
      'sceneLayoutCustomOverlay',
    ]),
    ...mapState('assetEditor', [
      'overlayTrackStandAlone',
      'workModel',
      'initialModel',
      'sceneLayoutSTPModel',
      'pages',
    ]),

    classObj() {
      return this.currentPage?.id;
    },

    styleObj() {
      return {
        '--curtains-gap': this.curtainsGap,
        'pointer-events': this.isMouseDisabled ? 'none' : 'all',
      };
    },

    currentPageComponent() {
      if (_.isObject(this.currentPage)) {
        if (this.currentPage.id === 'crop') {
          this.$nextTick(() => {
            app.analytics('analytics.scenes.clickedCrop', { from: 'top-menu' });
            const { x: left, y: top } = this.$el
              .querySelector('.wt-rtp-display')
              .getBoundingClientRect();
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            const cropModalProps = {
              sendNull: true,
              main: {
                ...this.assetModel.attributes,
                agd: this.AGD,
              },
              offset: {
                left,
                top,
              },
              visibleCurtains: true,
            };
            if (this.sceneLayoutCustomOverlay) {
              cropModalProps.visibleCurtains = false;
              cropModalProps.sceneLayoutSTPModel = this.sceneLayoutSTPModel;
              const layoutPreset = this.presets.find(
                (p) => p.id === this.sceneLayoutCustomOverlay.id
              );
              if (!layoutPreset) {
                throw new Error(
                  `Layout preset not found in theme info: ${this.sceneLayoutCustomOverlay.id}`
                );
              }
              const overlay = {
                type: 'wallpapermetaasset',
                metadata: {},
                wallpaperMetaData: {
                  type: 'SOLID',
                  wallpaperData: {
                    colorValue: 'rgba(0,0,0,1)',
                  },
                },
                displayStartTime: this.assetModel.displayStartTime,
                displayDuration: this.assetModel.attributes.displayDuration,
                displayEndTime: this.assetModel.attributes.displayEndTime,
              };
              cropModalProps.disableOverlay = true;
              cropModalProps.selectedAsset = 'main';
              cropModalProps.overlay = {
                ...overlay,
                sceneLayout: {
                  overlayAssetBoundingBox: {
                    top: 0,
                    bottom: 1,
                    left: 0,
                    right: 1,
                    zIndex: 1,
                  },
                  mainAssetBoundingBox: {
                    ...layoutPreset.cropRectangle,
                    zIndex: 2,
                  },
                  switchMainAndOverlay: false,
                },
                agd: this.AGD,
              };
            }
            this.cropModal = openCrop(cropModalProps).$on(
              'close',
              (e, { main } = {}) => {
                this.cropModal && this.cropModal.$destroy();
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.cropModal = null;
                this.changeCurrentPage('preview');
                if (!main) {
                  return;
                }
                this.setAGD({ ...main });
                bus.$emit('apply-click');
                // social assets need to re-create PRD upon scale change
                // let the assetModel re-generate its preview for a new one using new crop values
                if (
                  this.assetModel.isSOCIAL() ||
                  this.assetModel.isSOCIALFRAGMENT()
                ) {
                  // we use the original assetModel for clean rtp
                  this.assetModel.set('agd', { ...main });
                  workPool.job.cropSocialAsset.execute({
                    asset: this.assetModel.attributes,
                    programmer: app.rootStore.getters.storyProgrammer,
                  });
                }
              }
            );
          });
          return this.defaultEditorUrl + '-page';
        }
        const id = this.currentPage.custom
          ? 'custom'
          : _.kebabCase(this.currentPage.id);
        return id + '-page';
      } else {
        // default to preview
        return this.defaultEditorUrl + '-page';
      }
    },
  },

  watch: {
    currentPage() {
      this.setEditorUrl();
    },
  },

  created() {
    this.setRTPModel();
    this.setSceneLayoutSTP();
    this.setEditorUrl();
    this.setCustomPages();
    this.setOldDuration(this.workModel.displayDuration);
    this.SET_TEXT_IMPORT_OPTIONS();
  },

  beforeMount() {
    if (this.overlayGuid) {
      this.openOverlayAssetPage({ guid: this.overlayGuid, standalone: true });
    }
    app.keyboardAccessibility.add(app.KEYBOARD_LAYER.AE, [
      // prevent backspace from deleting timeline assets
      { on: 'keydown', key: $.ui.keyCode.BACKSPACE, run: _.noop },
      { on: 'keydown', key: $.ui.keyCode.DELETE, run: _.noop },
    ]);

    bus.$on('apply-click', this.applyClick);
    storyViewBus.$on('svbObjectTrackingDone', this.onObjectTrackingDone);
    bus.$on('done', this.done);
    bus.$on('cancel', () => {
      this.$emit('close');
    });
  },

  mounted() {
    document.addEventListener('paste', this.onPaste);
  },

  beforeDestroy() {
    // bus.$off() below is a blanket call on the asset editor bus and does not
    // reach storyViewBus
    storyViewBus.$off('svbObjectTrackingDone', this.onObjectTrackingDone);
    this.removeEditorUrl();
    document.removeEventListener('paste', this.onPaste);
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.AE);
    bus.$off();
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setShouldPreviewAutoplay',
      'assignRTPModel',
      'setAGD',
      'setOldDuration',
      'assignSceneLayoutSTPModel',
    ]),
    ...mapActions('assetEditor', [
      'REGENERATE_RTP',
      'generateRTPByWorkModel',
      'generateSceneLayoutSTP',
      'addPages',
      'changeCurrentPage',
      'openOverlayAssetPage',
      'APPLY_ASSET_CHANGES',
      'SET_TEXT_IMPORT_OPTIONS',
    ]),

    /**
     * Called when sub-effects are done
     * changes current effect to preview & submits a rtp request
     */
    applyClick() {
      this.setShouldPreviewAutoplay(true);

      // SIDE EFFECT: updates workModel with applied changes
      bus.$emit('apply');

      if (!this.overlayTrackStandAlone) {
        this.generateRTPByWorkModel();
      }
      this.setSceneLayoutSTP();
    },

    /**
     * A tracking job finished while this editor is open. The preview on screen
     * was rendered while the job was still running, so it has to be rebuilt.
     */
    onObjectTrackingDone({ trackingId, assetGuid }) {
      const isMine =
        (!!assetGuid && this.workModel.guid === assetGuid) ||
        app.objectTrackingController.carriesTrackingId(
          this.workModel,
          trackingId
        );

      if (!isMine) {
        return; // another asset's job - this editor may have been reopened since
      }

      this.REGENERATE_RTP();
    },

    done() {
      if (this.isDirty) {
        this.APPLY_ASSET_CHANGES();
      }

      this.$emit('close');
    },

    setCustomPages() {
      const blackListedPages = this.blackListPages('customOverlays');
      const newPages = [...this.presets, ...blackListedPages].map((p) => {
        return Object.assign(_.clone(p), {
          groupBy: p.layers && p.layers.length ? [p.layers[0]] : [],
          agdAttributes: ['customOverlays.' + p.id],
          custom: true,
        });
      });

      this.addPages(newPages);
    },

    /**
     * Assigns state's initial RTP model
     */
    setRTPModel() {
      // no agd? no aspect ratio? no game.
      if (!this.shouldPullRTP) {
        return;
      }

      // is current realTimePreviewResultData is up to date? use it to create a ready rtpmodel
      if (this.workModel.realTimePreviewChecksum) {
        this.assignRTPModel(
          app.realTimePreviewController.addPreviewModel({
            realTimePreviewResultData: this.workModel.realTimePreviewResultData,
            status: 'DONE',
            realTimePreviewChecksum: this.calRTPChecksum,
            visualTrack: this.workModel,
            type: app.RealtimePreviewType.VIDEO,
          })
        );
      } else {
        // realTimePreviewResultData is outdated, basa.
        const rtp = app.realTimePreviewController.getPreviewModelByChecksum(
          this.calRTPChecksum
        );
        // existing rtp model found on app, assign it to state
        if (rtp) {
          app.realTimePreviewController.increaseRtpHits(this.calRTPChecksum);
          this.assignRTPModel(rtp.model);
        } else {
          // rtp model not found on app, use it to create a new rtpmodel based on workmodel
          this.generateRTPByWorkModel();
        }
      }
    },

    setSceneLayoutSTP() {
      if (!this.sceneLayoutCustomOverlay) {
        this.assignSceneLayoutSTPModel(null);
        return;
      }
      const sceneLayoutSTP =
        app.realTimePreviewController.getPreviewModelByChecksum(
          this.calcSceneLayoutSTPChecksum,
          app.RealtimePreviewType.STATIC_MASK
        );

      if (sceneLayoutSTP) {
        this.assignSceneLayoutSTPModel(sceneLayoutSTP.model);
      } else {
        this.generateSceneLayoutSTP();
      }
    },

    /**
     * Removes Asset Editor location
     */
    removeEditorUrl() {
      app.navigate('stories/' + this.story.id, {
        trigger: false,
        closeModals: false,
      });
    },

    getCurrentPageId() {
      const isEditorUrlEditable = this.currentPage && !this.currentPage.custom;

      return isEditorUrlEditable ? this.currentPage.id : this.defaultEditorUrl;
    },

    /**
     * Sets Asset Editor location
     */
    setEditorUrl() {
      const wm = this.workModel;
      const currentPageId = this.getCurrentPageId();
      const storyPath = 'stories/' + app.storyController.story.id;
      const assetEditPath = `${wm.id}/${wm.displayStartTime}/${currentPageId}`;

      app.navigate(storyPath + '/edit/' + assetEditPath, {
        trigger: false,
        closeModals: false,
      });
    },

    onPaste(e) {
      bus.$emit('paste', e);
    },
  },
});
</script>

<style lang="scss" scoped></style>
